import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { listThemeReviews, saveEditorialRevision, saveThemeDecision } from "@/lib/theme-review.functions";
import { PENDING_REVISIONS } from "@/lib/theme-review-notes/pending";
import { REVIEW_NOTES, PLANET_SCOPE } from "@/lib/theme-review-notes";
import { detectDoubts, themeDistribution, themeDecisionSchema, type Doubt, type ThemeKey, type ReviewStatus as Status } from "@/lib/theme-review";

export const Route = createFileRoute("/admin/temalar")({
  head: () => ({ meta: [{ title: "Tema onayı · Yönetim" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ThemeReview,
});

const THEMES: Record<ThemeKey, { name: string; dot: string; chip: string }> = {
  planet: { name: "İnsan ve Gezegen", dot: "bg-theme-planet", chip: "border-theme-planet/40 text-theme-planet" },
  tech: { name: "Teknoloji ve Güç", dot: "bg-theme-tech", chip: "border-theme-tech/40 text-theme-tech" },
  economy: { name: "Ekonomi ve Toplum", dot: "bg-theme-economy", chip: "border-theme-economy/40 text-theme-economy" },
  meaning: { name: "İnsan ve Anlam", dot: "bg-theme-meaning", chip: "border-theme-meaning/40 text-theme-meaning" },
};
const KEYS = Object.keys(THEMES) as ThemeKey[];
const STATUS: Record<Status, string> = { oneri: "Öneri", onayli: "Onaylandı", incelemede: "İncelemede" };
type Draft = { primary: ThemeKey | null; secondary: ThemeKey | null };

function Chip({ k }: { k: ThemeKey | null }) {
  if (!k) return <span className="text-xs text-ink/40">— ikincil yok</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${THEMES[k].chip}`}>
      <span className={`size-2 rounded-full ${THEMES[k].dot}`} aria-hidden />
      {THEMES[k].name}
    </span>
  );
}

function ThemeReview() {
  const fetchAll = useServerFn(listThemeReviews);
  const save = useServerFn(saveThemeDecision);
  const saveRev = useServerFn(saveEditorialRevision);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["theme-reviews"], queryFn: () => fetchAll() });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [filter, setFilter] = useState<Status | "hepsi">("hepsi");
  const [view, setView] = useState<"kart" | "tablo">("kart");
  const [onlyDoubt, setOnlyDoubt] = useState(false);
  const [onlyPlanet, setOnlyPlanet] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, string>>({});

  const rows = useMemo(() => {
    const posts = new Map((data?.posts ?? []).map((p) => [p.id, p]));
    return (data?.reviews ?? [])
      .map((r) => ({ r, p: posts.get(r.post_id), hist: (data?.history ?? []).filter((h) => h.post_id === r.post_id) }))
      .sort((x, y) => (y.p?.published_at ?? "").localeCompare(x.p?.published_at ?? ""));
  }, [data]);
  const counts = rows.reduce((a, x) => ({ ...a, [x.r.status]: (a[x.r.status as Status] ?? 0) + 1 }), {} as Record<Status, number>);

  const doubts = useMemo(() => new Map(rows.map((x) => [x.r.post_id, detectDoubts(x.r.rationale)])), [rows]);
  const dist = themeDistribution(rows.map((x) => x.r));
  const visible = rows.filter((x) => (filter === "hepsi" || x.r.status === filter) && (!onlyPlanet || x.r.suggested_primary === "planet") && (!onlyDoubt || (doubts.get(x.r.post_id)?.flags.length ?? 0) > 0));
  const doubtCount = rows.filter((x) => (doubts.get(x.r.post_id)?.flags.length ?? 0) > 0).length;
  const srcLabel = (s: string) => (s === "editor_tam_metin" ? "Editör (tam metin)" : "Yapay zekâ önerisi");

  async function saveRevision(postId: string) {
    const p = PENDING_REVISIONS[postId];
    setBusy(postId);
    try {
      await saveRev({ data: { postId, ...p } });
      setMsg((m) => ({ ...m, [postId]: "" }));
      await qc.invalidateQueries({ queryKey: ["theme-reviews"] });
    } catch (e) {
      setMsg((m) => ({ ...m, [postId]: e instanceof Error ? e.message : "Kaydedilemedi" }));
    } finally { setBusy(null); }
  }

  async function decide(postId: string, status: Status, d: Draft) {
    const parsed = themeDecisionSchema.safeParse({ postId, status, primary: status === "onayli" ? d.primary : null, secondary: status === "onayli" ? d.secondary : null });
    if (!parsed.success) { setMsg((m) => ({ ...m, [postId]: parsed.error.issues[0].message })); return; }
    setBusy(postId);
    try {
      await save({ data: parsed.data });
      setMsg((m) => ({ ...m, [postId]: "" }));
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ["theme-reviews"] });
    } catch (e) {
      setMsg((m) => ({ ...m, [postId]: e instanceof Error ? e.message : "Kaydedilemedi" }));
    } finally { setBusy(null); }
  }

  if (isLoading) return <p className="text-sm text-ink/60">Yükleniyor…</p>;
  if (error) return <p className="text-sm text-clay">Tema kayıtları okunamadı.</p>;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-serif text-3xl">Tema onayı</h1>
        <p className="mt-1 text-sm text-ink/60">Yapay zekâ yalnızca önerir; tema yalnızca sizin onayınızla atanır. Okuyucuya yalnızca onaylı temalar gösterilir. Mevcut kategori ayrı kalır. COP31 ayrı koleksiyondur, tema değildir.</p>
      </header>
      <section aria-label="Tema dağılımı" className="grid gap-3 sm:grid-cols-2">
        {([["Önerilen birincil (öneri, onay değil)", dist.suggested], ["Onaylanmış birincil", dist.approved]] as const).map(([label, c]) => (
          <div key={label} className="rounded-xl border border-border p-3">
            <p className="text-[11px] uppercase tracking-wider text-ink/50">{label}</p>
            <ul className="mt-2 space-y-1 text-sm">
              {KEYS.map((k) => (
                <li key={k} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2"><span className={`size-2 shrink-0 rounded-full ${THEMES[k].dot}`} aria-hidden /><span className="truncate">{THEMES[k].name}</span></span>
                  <span className="tabular-nums">{c[k]}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
      <div className="flex flex-wrap gap-2 text-sm">
        {(["hepsi", "oneri", "incelemede", "onayli"] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-full border px-3 py-1 ${filter === f ? "border-ink bg-ink text-paper" : "border-border"}`}>
            {f === "hepsi" ? `Hepsi (${rows.length})` : `${STATUS[f]} (${counts[f] ?? 0})`}
          </button>
        ))}
        <button onClick={() => setOnlyDoubt(!onlyDoubt)} aria-pressed={onlyDoubt} className={`rounded-full border px-3 py-1 ${onlyDoubt ? "border-theme-economy bg-theme-economy/15 text-theme-economy" : "border-border"}`}>Sınırda kalanlar ({doubtCount})</button>
        <button onClick={() => setOnlyPlanet(!onlyPlanet)} aria-pressed={onlyPlanet} className={`rounded-full border px-3 py-1 ${onlyPlanet ? "border-theme-planet bg-theme-planet/15 text-theme-planet" : "border-border"}`}>İnceleme: İnsan ve Gezegen ({dist.suggested.planet})</button>
        <span className="ml-auto inline-flex overflow-hidden rounded-full border border-border">
          {(["kart", "tablo"] as const).map((v) => (
            <button key={v} onClick={() => setView(v)} aria-pressed={view === v} className={`px-3 py-1 ${view === v ? "bg-ink text-paper" : ""}`}>{v === "kart" ? "Kartlar" : "Toplu tablo"}</button>
          ))}
        </span>
      </div>
      {onlyPlanet && (
        <details open className="rounded-xl border border-theme-planet/40 p-4 text-sm">
          <summary className="cursor-pointer font-medium text-theme-planet">İnsan ve Gezegen: kapsam ve sınırlar (13 yazının karşılaştırması · yapay zekâ destekli, kesin değil)</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-ink/80">{PLANET_SCOPE.map((t) => <li key={t}>{t}</li>)}</ul>
        </details>
      )}
      <p className="text-xs text-ink/50">Sınırda işaretleri yalnızca kayıtlı gerekçe metninden okunur; hiçbir şeyi değiştirmez. Seçimler yalnızca “Onayla” veya “İncelemeye bırak” ile kaydedilir.</p>
      {view === "tablo" && <BulkView rows={visible} doubts={doubts} srcLabel={srcLabel} onOpen={(id) => { setView("kart"); setFilter("hepsi"); setOnlyDoubt(false); setTimeout(() => document.getElementById(`r-${id}`)?.scrollIntoView({ block: "start" }), 50); }} />}
      {view === "kart" && <ul className="space-y-3">
        {visible.map(({ r, p, hist }) => {
          const status = r.status as Status;
          // Approval must follow the editorial revision when one exists; the AI suggestion is only a fallback.
          const d: Draft = drafts[r.post_id] ?? (status === "onayli" ? { primary: r.approved_primary as ThemeKey, secondary: r.approved_secondary as ThemeKey | null } : r.editorial_primary ? { primary: r.editorial_primary as ThemeKey, secondary: r.editorial_secondary_open ? null : (r.editorial_secondary as ThemeKey | null) } : { primary: r.suggested_primary as ThemeKey, secondary: r.suggested_secondary as ThemeKey | null });
          const setD = (n: Draft) => setDrafts((x) => ({ ...x, [r.post_id]: n }));
          const sid = r.post_id;
          return (
          <li key={sid} id={`r-${sid}`} className="scroll-mt-4 rounded-xl border border-border p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 md:max-w-[60%]">
                <div className="flex flex-wrap items-center gap-2 text-xs text-ink/50">
                  <span>Kategori: {p?.category ?? "—"}</span>
                  {!p?.published && <span className="rounded bg-ink/5 px-1.5">taslak</span>}
                  <span className={`rounded px-1.5 ${status === "onayli" ? "bg-theme-planet/15 text-theme-planet" : status === "incelemede" ? "bg-theme-economy/15 text-theme-economy" : "bg-ink/5"}`}>{STATUS[status]}</span>
                </div>
                <Link to="/admin/yazi/$id" params={{ id: p?.id ?? "" }} className="mt-1 block font-serif text-lg leading-snug hover:underline">{p?.title_tr ?? "—"}</Link>
                <p className="mt-1 line-clamp-2 text-sm text-ink/70">{p?.excerpt_tr}</p>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  {p && <Link to="/admin/yazi/$id" params={{ id: p.id }} target="_blank" className="underline">Tam metni aç ↗</Link>}
                  {doubts.get(sid)?.note && <span className="text-theme-economy">Tereddüt: {doubts.get(sid)!.note}</span>}
                  {drafts[sid] && editing === sid && <span className="text-ink/50">Seçim henüz kaydedilmedi</span>}
                </div>
              </div>
              <div className="space-y-2 md:w-[38%]">
                {editing === sid ? (
                  <div className="space-y-2 text-sm">
                    <label className="block">Birincil
                      <select className="mt-1 w-full rounded border border-border bg-paper p-1.5" value={d.primary ?? ""} onChange={(e) => setD({ primary: e.target.value as ThemeKey, secondary: d.secondary === e.target.value ? null : d.secondary })}>
                        {KEYS.map((k) => <option key={k} value={k}>{THEMES[k].name}</option>)}
                      </select>
                    </label>
                    <label className="block">İkincil
                      <select className="mt-1 w-full rounded border border-border bg-paper p-1.5" value={d.secondary ?? ""} onChange={(e) => setD({ ...d, secondary: (e.target.value || null) as ThemeKey | null })}>
                        <option value="">Yok</option>
                        {KEYS.filter((k) => k !== d.primary).map((k) => <option key={k} value={k}>{THEMES[k].name}</option>)}
                      </select>
                    </label>
                  </div>
                ) : (
                  <div className="flex flex-col items-start gap-1.5">
                    <span className="text-[11px] uppercase tracking-wider text-ink/40">{status === "onayli" ? "Onaylı birincil" : "Önerilen birincil"}</span><Chip k={d.primary} />
                    <span className="text-[11px] uppercase tracking-wider text-ink/40">{status === "onayli" ? "Onaylı ikincil" : "Önerilen ikincil"}</span><Chip k={d.secondary} />
                  </div>
                )}
                <div className="grid gap-1.5 rounded-lg border border-border p-2 text-xs">
                  <div><span className="text-ink/50">1 · Yapay zekâ önerisi: </span>{THEMES[r.suggested_primary as ThemeKey]?.name}{r.suggested_secondary ? " + " + THEMES[r.suggested_secondary as ThemeKey]?.name : " (ikincil yok)"}</div>
                  <div><span className="text-ink/50">2 · Editoryal revizyon: </span>{r.editorial_primary ? <>{THEMES[r.editorial_primary as ThemeKey]?.name}{r.editorial_secondary ? " + " + THEMES[r.editorial_secondary as ThemeKey]?.name : r.editorial_secondary_open ? " (ikincil incelemeye açık)" : " (ikincil yok)"}{r.editorial_at && <span className="text-ink/40"> · {new Date(r.editorial_at).toLocaleString("tr-TR")}</span>}</> : <span className="text-ink/40">yok</span>}</div>
                  <div><span className="text-ink/50">3 · Kesin onay: </span>{status === "onayli" && r.approved_primary ? <>{THEMES[r.approved_primary as ThemeKey]?.name}{r.approved_secondary ? " + " + THEMES[r.approved_secondary as ThemeKey]?.name : ""}</> : <span className="text-ink/40">yok</span>}</div>
                  {PENDING_REVISIONS[sid] && !r.editorial_primary && (() => { const pr = PENDING_REVISIONS[sid]; return (
                    <div className="mt-1 rounded border border-dashed border-theme-economy/60 p-2">
                      <p className="font-medium text-theme-economy">Kaydedilmeye hazır editoryal karar (henüz kaydedilmedi, onay değil)</p>
                      <p>{THEMES[pr.primary].name}{pr.secondary ? " + " + THEMES[pr.secondary].name : pr.secondaryOpen ? " · ikincil incelemeye açık" : " · ikincil yok"}</p>
                      <button disabled={busy === sid} onClick={() => void saveRevision(sid)} className="mt-1 rounded border border-border px-2 py-1">Revizyonu kaydet</button>
                    </div>); })()}
                </div>
                <details className="text-xs text-ink/70">
                  <summary className="cursor-pointer">
                    Gerekçe · <span className="rounded bg-ink/5 px-1">{r.rationale_source === "editor_tam_metin" ? "Editör incelemesi (tam metin)" : "Yapay zekâ önerisi (tam metinden) — kesin değil"}</span>
                  </summary>
                  <p className="mt-1 whitespace-pre-line">{r.rationale}</p>
                </details>
                {REVIEW_NOTES[sid] && (
                  <details className="rounded-lg bg-theme-planet/5 p-2 text-xs text-ink/80">
                    <summary className="cursor-pointer font-medium">Editoryal inceleme notu · yapay zekâ destekli, kesin değil</summary>
                    <dl className="mt-1 space-y-1">
                      {([["Temel tez", "tez"], ["Birincil gerekçe (tam metin)", "birincil_gerekce"], ["Başka tema ihtimali", "alternatif"], ["İkincil gerekli mi?", "ikincil_gerekli"], ["Temadaki yeri", "kapsam_rolu"]] as const).map(([l, k]) => (
                        <div key={k}><dt className="text-ink/50">{l}</dt><dd>{REVIEW_NOTES[sid][k]}</dd></div>
                      ))}
                    </dl>
                    {REVIEW_NOTES[sid].editor_on && <p className="mt-2 rounded border border-theme-planet/40 p-1.5"><span className="text-ink/50">Editör ön değerlendirmesi (onay değil): </span>{REVIEW_NOTES[sid].editor_on}</p>}
                    {REVIEW_NOTES[sid].derin && (() => { const d = REVIEW_NOTES[sid].derin!; return (
                      <div className="mt-2 space-y-1 border-t border-border pt-2">
                        <p className="font-medium">Karşılaştırmalı tam metin incelemesi (yeni ölçütle)</p>
                        <p><span className="text-ink/50">Temel soru: </span>{d.temel_soru}</p>
                        <p><span className="text-ink/50">Ana tez: </span>{d.ana_tez}</p>
                        <p><span className="text-ink/50">Gezegen mi, Ekonomi mi: </span>{d.ayrim}</p>
                        <p><span className="text-ink/50">Önerilen birincil (öneri): </span>{d.onerilen_birincil}</p>
                        <p><span className="text-ink/50">İkincil: </span>{d.ikincil_karar}</p>
                        <ul className="space-y-1">{d.alintilar.map((q) => <li key={q} className="border-l-2 border-theme-planet/50 pl-2 italic">“{q}”</li>)}</ul>
                      </div>); })()}
                  </details>
                )}
                {status === "onayli" && r.approved_at && <p className="text-xs text-ink/50">Onay: {new Date(r.approved_at).toLocaleString("tr-TR")}</p>}
                {hist.length > 0 && (
                  <details className="text-xs text-ink/60">
                    <summary className="cursor-pointer">Geçmiş ({hist.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                      {hist.map((h) => (
                        <li key={h.id}>{new Date(h.changed_at).toLocaleString("tr-TR")}: {h.kind === "editoryal_revizyon" ? `Editoryal revizyon: ${h.old_editorial_primary ? THEMES[h.old_editorial_primary as ThemeKey].name : "yok"} → ${h.new_editorial_primary ? THEMES[h.new_editorial_primary as ThemeKey].name : "yok"}${h.new_editorial_secondary ? " + " + THEMES[h.new_editorial_secondary as ThemeKey].name : h.new_editorial_secondary_open ? " (ikincil açık)" : ""}` : <>Karar: {STATUS[(h.old_status ?? "oneri") as Status]} → {STATUS[h.new_status as Status]}{h.new_primary ? ` (${THEMES[h.new_primary as ThemeKey].name}${h.new_secondary ? " + " + THEMES[h.new_secondary as ThemeKey].name : ""})` : ""}</>}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {msg[sid] && <p className="text-xs text-clay" role="alert">{msg[sid]}</p>}
                <div className="flex flex-wrap gap-2 pt-1 text-xs">
                  <button disabled={busy === sid} onClick={() => void decide(sid, "onayli", d)} className="rounded-lg bg-ink px-3 py-1.5 text-paper">Onayla</button>
                  <button onClick={() => setEditing(editing === sid ? null : sid)} className="rounded-lg border border-border px-3 py-1.5">{editing === sid ? "Bitti" : "Değiştir"}</button>
                  <button disabled={busy === sid} onClick={() => void decide(sid, "incelemede", d)} className="rounded-lg border border-border px-3 py-1.5">İncelemeye bırak</button>
                </div>
              </div>
            </div>
          </li>
          );
        })}
      </ul>}
    </div>
  );
}

const DOUBT_LABEL: Record<Doubt, string> = { ikincil_zayif: "İkincil zayıf", birincil_belirsiz: "Birincil belirsiz", editoryal: "Editoryal karar" };
type Row = { r: any; p?: { id: string; title_tr: string; category: string } };
function BulkView({ rows, doubts, srcLabel, onOpen }: { rows: Row[]; doubts: Map<string, { flags: Doubt[]; note: string | null }>; srcLabel: (s: string) => string; onOpen: (id: string) => void }) {
  const theme = (k: string | null) => (k ? THEMES[k as ThemeKey]?.name : "—");
  const note = (id: string) => {
    const d = doubts.get(id);
    return d?.flags.length ? <><span className="font-medium text-theme-economy">{d.flags.map((f) => DOUBT_LABEL[f]).join(", ")}</span>{d.note && <span className="block text-ink/60">{d.note}</span>}</> : <span className="text-ink/40">—</span>;
  };
  return (
    <>
      <div className="hidden md:block">
        <table className="w-full table-fixed border-collapse text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-ink/50">
            <tr className="border-b border-border">
              <th className="w-[26%] py-2 pr-2">Başlık</th><th className="w-[10%] pr-2">Kategori</th><th className="w-[13%] pr-2">Önerilen birincil</th><th className="w-[13%] pr-2">Önerilen ikincil</th><th className="w-[10%] pr-2">Kaynak</th><th className="w-[20%] pr-2">Tereddüt notu</th><th className="w-[8%]">Durum</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ r, p }) => (
              <tr key={r.post_id} className="border-b border-border align-top">
                <td className="py-2 pr-2"><button onClick={() => onOpen(r.post_id)} className="text-left font-serif hover:underline">{p?.title_tr ?? "—"}</button>{p && <Link to="/admin/yazi/$id" params={{ id: p.id }} target="_blank" className="block text-xs text-ink/50 underline">Tam metin ↗</Link>}</td>
                <td className="pr-2 py-2 text-xs">{p?.category ?? "—"}</td>
                <td className="pr-2 py-2 text-xs">{theme(r.suggested_primary)}</td>
                <td className="pr-2 py-2 text-xs">{theme(r.suggested_secondary)}</td>
                <td className="pr-2 py-2 text-xs">{srcLabel(r.rationale_source)}</td>
                <td className="pr-2 py-2 text-xs">{note(r.post_id)}</td>
                <td className="py-2 text-xs">{STATUS[r.status as Status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-2 md:hidden">
        {rows.map(({ r, p }) => (
          <li key={r.post_id}>
            <details className="rounded-lg border border-border p-3">
              <summary className="cursor-pointer list-none">
                <span className="block font-serif leading-snug">{p?.title_tr ?? "—"}</span>
                <span className="mt-1 block text-xs text-ink/50">{theme(r.suggested_primary)} · {STATUS[r.status as Status]}{doubts.get(r.post_id)?.flags.length ? " · sınırda" : ""}</span>
              </summary>
              <dl className="mt-2 space-y-1 text-xs">
                <div><dt className="inline text-ink/50">Kategori: </dt><dd className="inline">{p?.category ?? "—"}</dd></div>
                <div><dt className="inline text-ink/50">Önerilen ikincil: </dt><dd className="inline">{theme(r.suggested_secondary)}</dd></div>
                <div><dt className="inline text-ink/50">Kaynak: </dt><dd className="inline">{srcLabel(r.rationale_source)}</dd></div>
                <div><dt className="text-ink/50">Tereddüt notu:</dt><dd>{note(r.post_id)}</dd></div>
              </dl>
              <div className="mt-2 flex gap-3 text-xs">
                <button onClick={() => onOpen(r.post_id)} className="underline">Gerekçe ve karar</button>
                {p && <Link to="/admin/yazi/$id" params={{ id: p.id }} target="_blank" className="underline">Tam metin ↗</Link>}
              </div>
            </details>
          </li>
        ))}
      </ul>
    </>
  );
}
